// SPDX-License-Identifier: Apache-2.0

// tether-brain v2 — embeddings (ingest-time only, CPU/transformers.js).
//
// Forge-stream scope: produce int8-quantized vectors and persist them. The
// runtime *search* over those vectors is Prometheus's domain (`cosineSim` +
// `topK` WGSL kernels in `packages/kernels/`). This file ends at the point
// where the int8 buffer hits Dexie.
//
// Quantization is symmetric per-vector:
//
//     scale = max(|v[i]|) for i in [0, dim)
//     q[i]  = round(v[i] * 127 / scale)        // int8, range [-127, 127]
//     v'[i] = q[i] * (scale / 127)             // dequant for sanity check
//
// Symmetric (not zero-point) keeps the WGSL kernel simple — one scalar per
// vector instead of two. e5-small outputs are L2-normalized so the per-vector
// max is roughly bounded; in practice we lose ~0.2% cosine fidelity at int8.

import { getDB } from './dexie-schema';
import type { VaultDoc, VaultEmbedding } from './schema';

// `@huggingface/transformers` is loaded dynamically — keeps the package free
// of an import-time dep on a 3 MB wasm bundle. Web app pulls it in lazily on
// first call to `embedDocs`.

type EmbedFn = (
  text: string,
  opts: { pooling?: 'mean' | 'cls'; normalize?: boolean },
) => Promise<{ data: Float32Array | number[] | Uint8Array }>;

interface TransformersModule {
  pipeline: (task: string, model: string) => Promise<EmbedFn>;
  env?: { allowLocalModels?: boolean };
}

let embedderPromise: Promise<EmbedFn> | null = null;
let activeModel: string | null = null;

async function getEmbedder(model: string): Promise<EmbedFn> {
  if (embedderPromise && activeModel === model) return embedderPromise;
  // Model swap — reset.
  embedderPromise = (async () => {
    const mod = (await import('@huggingface/transformers')) as TransformersModule;
    if (mod.env) mod.env.allowLocalModels = false;
    return mod.pipeline('feature-extraction', model);
  })();
  activeModel = model;
  return embedderPromise;
}

// ─── quantization ───────────────────────────────────────────────────────────

export interface QuantizedVec {
  vec: Int8Array;
  scale: number;
}

/** fp32 → int8, symmetric per-vector. `scale = max(|v|)`. */
export function quantizeInt8(v: Float32Array): QuantizedVec {
  let max = 0;
  for (let i = 0; i < v.length; i++) {
    const a = Math.abs(v[i] ?? 0);
    if (a > max) max = a;
  }
  const scale = max === 0 ? 1 : max;
  const out = new Int8Array(v.length);
  const f = 127 / scale;
  for (let i = 0; i < v.length; i++) {
    let q = Math.round((v[i] ?? 0) * f);
    if (q > 127) q = 127;
    else if (q < -127) q = -127;
    out[i] = q;
  }
  return { vec: out, scale };
}

/** int8 → fp32 (for diagnostics + the CPU-fallback search path). */
export function dequantizeInt8(q: Int8Array, scale: number): Float32Array {
  const out = new Float32Array(q.length);
  const f = scale / 127;
  for (let i = 0; i < q.length; i++) out[i] = (q[i] ?? 0) * f;
  return out;
}

// ─── embed + persist ───────────────────────────────────────────────────────

export interface EmbedDocsOptions {
  /** HuggingFace model id. Default is the multilingual e5-small (384 dim). */
  model?: string;
  /** Skip docs whose embedding row is newer than the doc's `updatedAt`. */
  skipFresh?: boolean;
  /** Progress reporter — `done` of `total` docs embedded so far. */
  onProgress?: (done: number, total: number) => void;
  signal?: AbortSignal;
}

const DEFAULT_MODEL = 'Xenova/multilingual-e5-small';

/**
 * Embed each doc, quantize to int8, persist to Dexie. Returns the count of
 * rows written (which can be < docs.length when `skipFresh` skips some).
 *
 * The CPU search kernel (Prometheus) reads the resulting `embeddings_int8`
 * table directly — we don't need to pass anything back here.
 */
export async function embedDocs(
  docs: VaultDoc[],
  opts: EmbedDocsOptions = {},
): Promise<number> {
  const model = opts.model ?? DEFAULT_MODEL;
  const db = getDB();
  const embed = await getEmbedder(model);

  let written = 0;
  for (let i = 0; i < docs.length; i++) {
    if (opts.signal?.aborted) throw new DOMException('aborted', 'AbortError');
    const doc = docs[i];
    if (!doc) continue;

    if (opts.skipFresh) {
      const existing = await db.embeddings_int8.get(`${doc.id}::${model}`);
      if (existing && existing.computedAt >= doc.updatedAt) {
        opts.onProgress?.(i + 1, docs.length);
        continue;
      }
    }

    const trimmed =
      doc.content.length > 4000 ? doc.content.slice(0, 4000) : doc.content;
    const out = await embed(`passage: ${trimmed}`, {
      pooling: 'mean',
      normalize: true,
    });
    const raw = out.data;
    const vec =
      raw instanceof Float32Array
        ? raw
        : Float32Array.from(raw as ArrayLike<number>);
    const { vec: q, scale } = quantizeInt8(vec);

    const row: VaultEmbedding = {
      id: `${doc.id}::${model}`,
      docId: doc.id,
      model,
      dim: q.length,
      vec: q,
      scale,
      computedAt: Date.now(),
    };
    await db.embeddings_int8.put(row);
    written++;
    opts.onProgress?.(i + 1, docs.length);
  }
  return written;
}

/** Drop every embedding row for a single doc (all models). */
export async function removeDocEmbeddings(docId: string): Promise<void> {
  const db = getDB();
  await db.embeddings_int8.where('docId').equals(docId).delete();
}

/** Pre-warm the model on idle. */
export function preloadEmbedder(model: string = DEFAULT_MODEL): void {
  if (typeof window === 'undefined') return;
  void getEmbedder(model);
}

export const EMBEDDING_DEFAULT_MODEL = DEFAULT_MODEL;
